import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { workRpc } from './api';
import type {
  FloorWorkstation,
  WorkBreak,
  WorkBreakReason,
  WorkCheckin,
  WorkCredentialStatus,
  WorkDefect,
  WorkDefectDisposition,
  WorkDefectSeverity,
  WorkDefectStatus,
  WorkInstruction,
  WorkInstructionStep,
  WorkInstructionWithSteps,
  WorkLeaderboardRow,
  WorkMoraleDay,
  WorkNote,
  WorkNoteKind,
  WorkNoteStatus,
  WorkProfileStats,
  WorkSession,
  WorkSessionSnapshot,
  WorkSessionStartResult,
  WorkShiftSummary,
  WorkStationDay,
  WorkUnit,
  ScrappedPart,
} from './types';

/**
 * Work Mode — data layer.
 *
 * Reads go straight at the tables (RLS: org-wide read). Every write that
 * belongs to a shift goes through a `work_*` RPC so the server can verify
 * the tablet / worker / manager relationship (see migration 116).
 */

export const workKeys = {
  all: ['work'] as const,
  stations: ['workstations'] as const, // shared with inventory's useWorkstations
  openSessions: () => ['work', 'sessions', 'open'] as const,
  stationOpenSession: (stationId: string | null) => ['work', 'sessions', 'station', stationId] as const,
  snapshot: (sessionId: string | null) => ['work', 'snapshot', sessionId] as const,
  sessionUnits: (sessionId: string | null) => ['work', 'units', 'session', sessionId] as const,
  sessionBreaks: (sessionId: string | null) => ['work', 'breaks', 'session', sessionId] as const,
  openBreaks: () => ['work', 'breaks', 'open'] as const,
  recentUnits: (limit: number) => ['work', 'units', 'recent', limit] as const,
  sessions: (days: number) => ['work', 'sessions', 'history', days] as const,
  instructionFor: (itemId: string | null, stationId: string | null) => ['work', 'instruction', itemId, stationId] as const,
  instructions: () => ['work', 'instructions'] as const,
  credentials: () => ['work', 'credentials'] as const,
  defects: (status: string) => ['work', 'defects', status] as const,
  notes: (status: string) => ['work', 'notes', status] as const,
  myCheckin: (userId: string | undefined) => ['work', 'checkin', 'me', userId] as const,
  checkins: (days: number) => ['work', 'checkins', days] as const,
  morale: (days: number) => ['work', 'morale', days] as const,
  leaderboard: (days: number) => ['work', 'leaderboard', days] as const,
  stats: (userId: string | null) => ['work', 'stats', userId] as const,
  stationDaily: (days: number) => ['work', 'station-daily', days] as const,
  signedUrl: (bucket: string, path: string) => ['work', 'signed', bucket, path] as const,
};

// ---------------------------------------------------------------------------
// Workstations
// ---------------------------------------------------------------------------

export function useFloorWorkstations() {
  return useQuery({
    queryKey: workKeys.stations,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workstations' as never)
        .select('*')
        .order('position', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      // Guard the array column in case the client is ahead of migration 144.
      return ((data as unknown as FloorWorkstation[]) ?? []).map((s) => ({ ...s, ppe: Array.isArray(s.ppe) ? s.ppe : [] }));
    },
  });
}

export function useConfigureWorkstation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      floor?: { x: number; y: number } | null;
      defaultItemId?: string | null;
      targetSeconds?: number | null;
      /** Text fields: undefined = leave alone, '' = clear. */
      description?: string;
      isActive?: boolean;
      locationNotes?: string;
      safetyNotes?: string;
      setupNotes?: string;
      /** undefined = leave alone, [] = clear. */
      ppe?: string[];
      /** undefined = leave alone, null = clear. */
      coverImagePath?: string | null;
    }) => {
      return workRpc<FloorWorkstation>('work_station_configure', {
        p_workstation_id: input.id,
        p_floor_x: input.floor?.x ?? null,
        p_floor_y: input.floor?.y ?? null,
        p_default_item_id: input.defaultItemId ?? null,
        p_clear_item: input.defaultItemId === null,
        p_target_seconds: input.targetSeconds ?? null,
        p_clear_target: input.targetSeconds === null,
        p_description: input.description ?? null,
        p_is_active: input.isActive ?? null,
        p_clear_floor: input.floor === null,
        p_location_notes: input.locationNotes ?? null,
        p_safety_notes: input.safetyNotes ?? null,
        p_setup_notes: input.setupNotes ?? null,
        p_ppe: input.ppe ?? null,
        p_cover_image_path: input.coverImagePath ?? null,
        p_clear_cover: input.coverImagePath === null,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workKeys.stations });
    },
  });
}

/** Breaks currently in progress across the org (for the live view). */
export function useOpenBreaks() {
  return useQuery({
    queryKey: workKeys.openBreaks(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('work_breaks' as never)
        .select('*')
        .is('ended_at', null);
      if (error) throw error;
      return data as unknown as WorkBreak[];
    },
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

/** Every shift currently open in the org — the live Floor. */
export function useOpenSessions() {
  return useQuery({
    queryKey: workKeys.openSessions(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('work_sessions' as never)
        .select('*')
        .is('ended_at', null)
        .order('started_at', { ascending: true });
      if (error) throw error;
      return data as unknown as WorkSession[];
    },
    refetchInterval: 30_000,
  });
}

/** The open shift at one station (so a reloaded tablet can resume it). */
export function useStationOpenSession(stationId: string | null) {
  return useQuery({
    queryKey: workKeys.stationOpenSession(stationId),
    enabled: !!stationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('work_sessions' as never)
        .select('*')
        .eq('workstation_id', stationId!)
        .is('ended_at', null)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as WorkSession | null) ?? null;
    },
  });
}

export function useSessionSnapshot(sessionId: string | null) {
  return useQuery({
    queryKey: workKeys.snapshot(sessionId),
    enabled: !!sessionId,
    queryFn: () => workRpc<WorkSessionSnapshot>('work_session_snapshot', { p_session_id: sessionId }),
    refetchInterval: 60_000,
  });
}

export function useSessionUnits(sessionId: string | null) {
  return useQuery({
    queryKey: workKeys.sessionUnits(sessionId),
    enabled: !!sessionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('work_units' as never)
        .select('*')
        .eq('session_id', sessionId!)
        .order('seq', { ascending: true });
      if (error) throw error;
      return data as unknown as WorkUnit[];
    },
  });
}

export function useSessionBreaks(sessionId: string | null) {
  return useQuery({
    queryKey: workKeys.sessionBreaks(sessionId),
    enabled: !!sessionId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('work_breaks' as never)
        .select('*')
        .eq('session_id', sessionId!)
        .order('started_at', { ascending: true });
      if (error) throw error;
      return data as unknown as WorkBreak[];
    },
  });
}

export function useRecentUnits(limit = 50) {
  return useQuery({
    queryKey: workKeys.recentUnits(limit),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('work_units' as never)
        .select('*')
        .order('completed_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data as unknown as WorkUnit[];
    },
  });
}

export function useSessionHistory(days = 14) {
  return useQuery({
    queryKey: workKeys.sessions(days),
    queryFn: async () => {
      const since = new Date(Date.now() - days * 86_400_000).toISOString();
      const { data, error } = await supabase
        .from('work_sessions' as never)
        .select('*')
        .gte('started_at', since)
        .order('started_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return data as unknown as WorkSession[];
    },
  });
}

function invalidateSession(qc: ReturnType<typeof useQueryClient>, sessionId?: string | null) {
  void qc.invalidateQueries({ queryKey: workKeys.openSessions() });
  void qc.invalidateQueries({ queryKey: workKeys.openBreaks() });
  void qc.invalidateQueries({ queryKey: ['work', 'sessions', 'station'] });
  if (sessionId) {
    void qc.invalidateQueries({ queryKey: workKeys.snapshot(sessionId) });
    void qc.invalidateQueries({ queryKey: workKeys.sessionUnits(sessionId) });
    void qc.invalidateQueries({ queryKey: workKeys.sessionBreaks(sessionId) });
  }
}

export function useStartSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      workstationId: string;
      userId?: string | null;
      pin?: string | null;
      badge?: string | null;
      itemId?: string | null;
    }) =>
      workRpc<WorkSessionStartResult>('work_session_start', {
        p_workstation_id: input.workstationId,
        p_user_id: input.userId ?? null,
        p_pin: input.pin ?? null,
        p_badge: input.badge ?? null,
        p_item_id: input.itemId ?? null,
      }),
    onSuccess: (res) => {
      if (res.ok) invalidateSession(qc, res.session.id);
    },
  });
}

export function useEndSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; reason?: string }) =>
      workRpc<WorkShiftSummary>('work_session_end', {
        p_session_id: input.sessionId,
        p_reason: input.reason ?? 'manual',
      }),
    onSuccess: (_res, vars) => {
      invalidateSession(qc, vars.sessionId);
      void qc.invalidateQueries({ queryKey: ['work', 'stats'] });
      void qc.invalidateQueries({ queryKey: ['work', 'leaderboard'] });
    },
  });
}

export function useSetSessionItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; itemId: string | null }) =>
      workRpc<WorkSessionSnapshot>('work_session_set_item', {
        p_session_id: input.sessionId,
        p_item_id: input.itemId,
      }),
    onSuccess: (snap, vars) => {
      qc.setQueryData(workKeys.snapshot(vars.sessionId), snap);
      invalidateSession(qc, vars.sessionId);
    },
  });
}

export function useCompleteUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string }) =>
      workRpc<WorkSessionSnapshot>('work_unit_complete', { p_session_id: input.sessionId }),
    onSuccess: (snap, vars) => {
      qc.setQueryData(workKeys.snapshot(vars.sessionId), snap);
      invalidateSession(qc, vars.sessionId);
    },
  });
}

/**
 * "+" for a serialized item: the scanned serial, the photos, the component
 * lots and the QC answer go with the unit (migration 142).
 */
export function useCompleteSerializedUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sessionId: string;
      serial: string;
      photoPaths: string[];
      labelPhotoPath?: string | null;
      componentLots: Array<{ item_id: string | null; sku: string; name: string; lot_number: string }>;
      qcResult: 'pass' | 'fail';
      qcNotes?: string | null;
      notes?: string | null;
    }) =>
      workRpc<WorkSessionSnapshot>('work_unit_complete_serialized', {
        p_session_id: input.sessionId,
        p_serial: input.serial,
        p_photo_paths: input.photoPaths,
        p_label_photo_path: input.labelPhotoPath ?? null,
        p_component_lots: input.componentLots,
        p_qc_result: input.qcResult,
        p_qc_notes: input.qcNotes ?? null,
        p_notes: input.notes ?? null,
      }),
    onSuccess: (snap, vars) => {
      qc.setQueryData(workKeys.snapshot(vars.sessionId), snap);
      invalidateSession(qc, vars.sessionId);
      void qc.invalidateQueries({ queryKey: ['serialized'] });
    },
  });
}

export function useUndoUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string }) =>
      workRpc<WorkSessionSnapshot>('work_unit_undo', { p_session_id: input.sessionId }),
    onSuccess: (snap, vars) => {
      qc.setQueryData(workKeys.snapshot(vars.sessionId), snap);
      invalidateSession(qc, vars.sessionId);
      void qc.invalidateQueries({ queryKey: ['serialized'] });
    },
  });
}

export function useStartBreak() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string; reason: WorkBreakReason }) =>
      workRpc<WorkSessionSnapshot>('work_break_start', {
        p_session_id: input.sessionId,
        p_reason: input.reason,
      }),
    onSuccess: (snap, vars) => {
      qc.setQueryData(workKeys.snapshot(vars.sessionId), snap);
      invalidateSession(qc, vars.sessionId);
    },
  });
}

export function useEndBreak() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sessionId: string }) =>
      workRpc<WorkSessionSnapshot>('work_break_end', { p_session_id: input.sessionId }),
    onSuccess: (snap, vars) => {
      qc.setQueryData(workKeys.snapshot(vars.sessionId), snap);
      invalidateSession(qc, vars.sessionId);
    },
  });
}

export function useReportDefect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sessionId: string;
      description: string;
      severity: WorkDefectSeverity;
      disposition: WorkDefectDisposition;
      progressPct: number | null;
      stepId: string | null;
      scrappedParts: ScrappedPart[];
      photoPaths: string[];
      restartUnit: boolean;
      scrapTags?: string[];
      needsRework?: boolean;
      reworkNotes?: string | null;
    }) =>
      workRpc<WorkSessionSnapshot>('work_defect_report', {
        p_session_id: input.sessionId,
        p_description: input.description,
        p_severity: input.severity,
        p_disposition: input.disposition,
        p_progress_pct: input.progressPct,
        p_step_id: input.stepId,
        p_scrapped_parts: input.scrappedParts,
        p_photo_paths: input.photoPaths,
        p_restart_unit: input.restartUnit,
        p_scrap_tags: input.scrapTags ?? [],
        p_needs_rework: input.needsRework ?? false,
        p_rework_notes: input.reworkNotes ?? null,
      }),
    onSuccess: (snap, vars) => {
      qc.setQueryData(workKeys.snapshot(vars.sessionId), snap);
      invalidateSession(qc, vars.sessionId);
      void qc.invalidateQueries({ queryKey: ['work', 'defects'] });
      void qc.invalidateQueries({ queryKey: ['scrap'] });
    },
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sessionId: string;
      kind: WorkNoteKind;
      body: string;
      stepId?: string | null;
      photoPaths?: string[];
    }) =>
      workRpc<WorkNote>('work_note_add', {
        p_session_id: input.sessionId,
        p_kind: input.kind,
        p_body: input.body,
        p_step_id: input.stepId ?? null,
        p_photo_paths: input.photoPaths ?? [],
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work', 'notes'] });
    },
  });
}

export function useCheckin() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: (input: { mood: number; note?: string | null; isPrivate?: boolean; sessionId?: string | null }) =>
      workRpc<WorkCheckin>('work_checkin', {
        p_mood: input.mood,
        p_note: input.note ?? null,
        p_is_private: input.isPrivate ?? false,
        p_session_id: input.sessionId ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: workKeys.myCheckin(user?.id) });
      void qc.invalidateQueries({ queryKey: ['work', 'checkins'] });
      void qc.invalidateQueries({ queryKey: ['work', 'morale'] });
    },
  });
}

/** Sends a heartbeat every few minutes while a shift is open on this tablet. */
export function useSessionHeartbeat(sessionId: string | null, intervalMs = 5 * 60_000) {
  useEffect(() => {
    if (!sessionId) return;
    const id = window.setInterval(() => {
      void workRpc('work_session_heartbeat', { p_session_id: sessionId }).catch(() => {
        /* the session may have been closed elsewhere; the snapshot poll will notice */
      });
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [sessionId, intervalMs]);
}

export function useCloseStaleSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { hours: number }) =>
      workRpc<number>('work_sessions_close_stale', { p_hours: input.hours }),
    onSuccess: () => invalidateSession(qc),
  });
}

// ---------------------------------------------------------------------------
// Credentials
// ---------------------------------------------------------------------------

export function useCredentialStatus() {
  return useQuery({
    queryKey: workKeys.credentials(),
    queryFn: async () => {
      const rows = await workRpc<WorkCredentialStatus[]>('work_credentials_status');
      const map = new Map<string, WorkCredentialStatus>();
      for (const r of rows ?? []) map.set(r.user_id, r);
      return map;
    },
    staleTime: 30_000,
  });
}

export function useSetPin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; pin: string }) =>
      workRpc<void>('work_credentials_set_pin', { p_user_id: input.userId, p_pin: input.pin }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workKeys.credentials() }),
  });
}

export function useSetBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; badge: string | null }) =>
      workRpc<void>('work_credentials_set_badge', { p_user_id: input.userId, p_badge: input.badge }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workKeys.credentials() }),
  });
}

export function useUnlockCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string }) =>
      workRpc<void>('work_credentials_unlock', { p_user_id: input.userId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workKeys.credentials() }),
  });
}

// ---------------------------------------------------------------------------
// Instructions
// ---------------------------------------------------------------------------

function attachSteps(instructions: WorkInstruction[], steps: WorkInstructionStep[]): WorkInstructionWithSteps[] {
  const byInstr = new Map<string, WorkInstructionStep[]>();
  for (const s of steps) {
    const arr = byInstr.get(s.instruction_id) ?? [];
    arr.push(s);
    byInstr.set(s.instruction_id, arr);
  }
  return instructions.map((i) => ({
    ...i,
    steps: (byInstr.get(i.id) ?? []).sort((a, b) => a.step_number - b.step_number),
  }));
}

/**
 * The instruction set to show on the bench for an item at a station: a
 * published station-specific set wins, then a published generic one.
 */
export function useWorkInstructionFor(itemId: string | null, stationId: string | null) {
  return useQuery({
    queryKey: workKeys.instructionFor(itemId, stationId),
    enabled: !!itemId,
    queryFn: async (): Promise<WorkInstructionWithSteps | null> => {
      const { data, error } = await supabase
        .from('work_instructions' as never)
        .select('*')
        .eq('item_id', itemId!)
        .eq('is_published', true);
      if (error) throw error;
      const list = (data as unknown as WorkInstruction[]) ?? [];
      const pick =
        list.find((i) => i.workstation_id === stationId) ?? list.find((i) => i.workstation_id === null) ?? list[0];
      if (!pick) return null;
      const steps = await supabase
        .from('work_instruction_steps' as never)
        .select('*')
        .eq('instruction_id', pick.id)
        .order('step_number', { ascending: true });
      if (steps.error) throw steps.error;
      return attachSteps([pick], (steps.data as unknown as WorkInstructionStep[]) ?? [])[0];
    },
    staleTime: 60_000,
  });
}

/** Every instruction set in the org with its steps (editor view). */
export function useWorkInstructions() {
  return useQuery({
    queryKey: workKeys.instructions(),
    queryFn: async (): Promise<WorkInstructionWithSteps[]> => {
      const [instr, steps] = await Promise.all([
        supabase.from('work_instructions' as never).select('*').order('updated_at', { ascending: false }),
        supabase.from('work_instruction_steps' as never).select('*'),
      ]);
      if (instr.error) throw instr.error;
      if (steps.error) throw steps.error;
      return attachSteps(
        (instr.data as unknown as WorkInstruction[]) ?? [],
        (steps.data as unknown as WorkInstructionStep[]) ?? [],
      );
    },
  });
}

function invalidateInstructions(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: workKeys.instructions() });
  void qc.invalidateQueries({ queryKey: ['work', 'instruction'] });
}

export function useSaveInstruction() {
  const qc = useQueryClient();
  const { user, profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      itemId: string;
      workstationId: string | null;
      title: string;
      summary?: string | null;
      safetyNotes?: string | null;
      targetSeconds?: number | null;
      isPublished?: boolean;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const title = input.title.trim();
      if (!title) throw new Error('Give the instructions a title.');
      const payload = {
        organization_id: profile.organization_id,
        item_id: input.itemId,
        workstation_id: input.workstationId,
        title,
        summary: input.summary?.trim() || null,
        safety_notes: input.safetyNotes?.trim() || null,
        target_seconds: input.targetSeconds ?? null,
        is_published: input.isPublished ?? false,
        updated_by: user?.id ?? null,
      };
      if (input.id) {
        const { data, error } = await supabase
          .from('work_instructions' as never)
          .update(payload as never)
          .eq('id', input.id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as WorkInstruction;
      }
      const { data, error } = await supabase
        .from('work_instructions' as never)
        .insert({ ...payload, created_by: user?.id ?? null } as never)
        .select('*')
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          throw new Error('There is already an instruction set for that item at that station.');
        }
        throw error;
      }
      return data as unknown as WorkInstruction;
    },
    onSuccess: () => invalidateInstructions(qc),
  });
}

export function useDeleteInstruction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { error } = await supabase.from('work_instructions' as never).delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => invalidateInstructions(qc),
  });
}

export function useSaveStep() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      instructionId: string;
      stepNumber: number;
      title: string;
      body?: string | null;
      caution?: string | null;
      imagePath?: string | null;
      estSeconds?: number | null;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const title = input.title.trim();
      if (!title) throw new Error('Give the step a title.');
      const payload = {
        organization_id: profile.organization_id,
        instruction_id: input.instructionId,
        step_number: input.stepNumber,
        title,
        body: input.body?.trim() || null,
        caution: input.caution?.trim() || null,
        image_path: input.imagePath ?? null,
        est_seconds: input.estSeconds ?? null,
      };
      const q = input.id
        ? supabase.from('work_instruction_steps' as never).update(payload as never).eq('id', input.id)
        : supabase.from('work_instruction_steps' as never).insert(payload as never);
      const { data, error } = await q.select('*').single();
      if (error) throw error;
      return data as unknown as WorkInstructionStep;
    },
    onSuccess: () => invalidateInstructions(qc),
  });
}

export function useDeleteStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; imagePath?: string | null }) => {
      const { error } = await supabase.from('work_instruction_steps' as never).delete().eq('id', input.id);
      if (error) throw error;
      if (input.imagePath) {
        await supabase.storage.from('work-instructions').remove([input.imagePath]);
      }
    },
    onSuccess: () => invalidateInstructions(qc),
  });
}

/** Renumber steps 1..n in the given order (unique constraint is deferrable). */
export function useReorderSteps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orderedIds: string[] }) => {
      // Two passes keep every intermediate state unique even though the
      // constraint is deferred: park at a high offset, then settle.
      const offset = 1000;
      for (let i = 0; i < input.orderedIds.length; i++) {
        const { error } = await supabase
          .from('work_instruction_steps' as never)
          .update({ step_number: offset + i + 1 } as never)
          .eq('id', input.orderedIds[i]);
        if (error) throw error;
      }
      for (let i = 0; i < input.orderedIds.length; i++) {
        const { error } = await supabase
          .from('work_instruction_steps' as never)
          .update({ step_number: i + 1 } as never)
          .eq('id', input.orderedIds[i]);
        if (error) throw error;
      }
    },
    onSuccess: () => invalidateInstructions(qc),
  });
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

function safeFilename(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'file';
}

/** Upload a step photo. Returns the object key to store on the step. */
export async function uploadInstructionImage(orgId: string, instructionId: string, file: File): Promise<string> {
  const path = `${orgId}/${instructionId}/${Date.now()}-${safeFilename(file.name)}`;
  const { error } = await supabase.storage.from('work-instructions').upload(path, file, {
    upsert: false,
    cacheControl: '3600',
    contentType: file.type || 'application/octet-stream',
  });
  if (error) throw error;
  return path;
}

/** Upload a bench capture (defect / note photo). Returns the object key. */
export async function uploadCapture(orgId: string, sessionId: string, file: File | Blob, name = 'photo.jpg'): Promise<string> {
  const path = `${orgId}/${sessionId}/${Date.now()}-${safeFilename(name)}`;
  const { error } = await supabase.storage.from('work-captures').upload(path, file, {
    upsert: false,
    cacheControl: '3600',
    contentType: (file as File).type || 'image/jpeg',
  });
  if (error) throw error;
  return path;
}

export function useSignedUrl(bucket: 'work-instructions' | 'work-captures', path: string | null | undefined) {
  return useQuery({
    queryKey: workKeys.signedUrl(bucket, path ?? ''),
    enabled: !!path,
    staleTime: 4 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path!, 60 * 5);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}

// ---------------------------------------------------------------------------
// Defects / notes / check-ins (manager views)
// ---------------------------------------------------------------------------

export function useWorkDefects(status: WorkDefectStatus | 'all' = 'all') {
  return useQuery({
    queryKey: workKeys.defects(status),
    queryFn: async () => {
      let q = supabase.from('work_defects' as never).select('*').order('created_at', { ascending: false }).limit(300);
      if (status !== 'all') q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as WorkDefect[];
    },
  });
}

export function useReviewDefect() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { id: string; status: WorkDefectStatus; reviewNotes?: string | null }) => {
      const { data, error } = await supabase
        .from('work_defects' as never)
        .update({
          status: input.status,
          review_notes: input.reviewNotes ?? null,
          reviewed_by: user?.id ?? null,
          reviewed_at: new Date().toISOString(),
        } as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as WorkDefect;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['work', 'defects'] }),
  });
}

export function useWorkNotes(status: WorkNoteStatus | 'all' | 'open' = 'all') {
  return useQuery({
    queryKey: workKeys.notes(status),
    queryFn: async () => {
      let q = supabase.from('work_notes' as never).select('*').order('created_at', { ascending: false }).limit(300);
      if (status === 'open') q = q.in('status', ['new', 'acknowledged', 'planned']);
      else if (status !== 'all') q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return data as unknown as WorkNote[];
    },
  });
}

export function useRespondNote() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: { id: string; status: WorkNoteStatus; response?: string | null }) => {
      const { data, error } = await supabase
        .from('work_notes' as never)
        .update({
          status: input.status,
          manager_response: input.response ?? null,
          responded_by: user?.id ?? null,
          responded_at: new Date().toISOString(),
        } as never)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as WorkNote;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['work', 'notes'] });
      void qc.invalidateQueries({ queryKey: ['work', 'stats'] });
    },
  });
}

/** Today's check-in for the signed-in user (regular app, not the kiosk). */
export function useMyCheckinToday() {
  const { user } = useAuth();
  return useQuery({
    queryKey: workKeys.myCheckin(user?.id),
    enabled: !!user?.id,
    queryFn: async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('work_checkins' as never)
        .select('*')
        .eq('user_id', user!.id)
        .eq('checkin_date', today)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as WorkCheckin | null) ?? null;
    },
  });
}

/** Non-private check-ins visible to the caller (managers) for the period. */
export function useWorkCheckins(days = 30) {
  return useQuery({
    queryKey: workKeys.checkins(days),
    queryFn: async () => {
      const since = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('work_checkins' as never)
        .select('*')
        .gte('checkin_date', since)
        .order('checkin_date', { ascending: false })
        .limit(500);
      if (error) throw error;
      return data as unknown as WorkCheckin[];
    },
  });
}

export function useMoraleTrend(days = 30) {
  return useQuery({
    queryKey: workKeys.morale(days),
    queryFn: () => workRpc<WorkMoraleDay[]>('work_morale_trend', { p_days: days }),
  });
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export function useWorkProfileStats(userId: string | null) {
  return useQuery({
    queryKey: workKeys.stats(userId),
    enabled: !!userId,
    queryFn: () => workRpc<WorkProfileStats>('work_profile_stats', { p_user_id: userId }),
    staleTime: 30_000,
  });
}

export function useLeaderboard(days = 7) {
  return useQuery({
    queryKey: workKeys.leaderboard(days),
    queryFn: () => workRpc<WorkLeaderboardRow[]>('work_leaderboard', { p_days: days }),
    staleTime: 30_000,
  });
}

export function useStationDaily(days = 14) {
  return useQuery({
    queryKey: workKeys.stationDaily(days),
    queryFn: () => workRpc<WorkStationDay[]>('work_station_daily', { p_days: days }),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Realtime
// ---------------------------------------------------------------------------

/**
 * Subscribe to the hot Work Mode tables for the org and invalidate the
 * affected queries. One channel per mounted consumer; cheap enough.
 */
export function useWorkRealtime(enabled = true) {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  useEffect(() => {
    if (!enabled || !orgId) return;
    const channel = supabase
      .channel(`work:${orgId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'work_sessions', filter: `organization_id=eq.${orgId}` },
        (payload) => {
          const row = (payload.new ?? payload.old) as Partial<WorkSession>;
          invalidateSession(qc, row?.id ?? null);
          void qc.invalidateQueries({ queryKey: ['work', 'sessions', 'history'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'work_units', filter: `organization_id=eq.${orgId}` },
        (payload) => {
          const row = (payload.new ?? payload.old) as Partial<WorkUnit>;
          if (row?.session_id) {
            void qc.invalidateQueries({ queryKey: workKeys.snapshot(row.session_id) });
            void qc.invalidateQueries({ queryKey: workKeys.sessionUnits(row.session_id) });
          }
          void qc.invalidateQueries({ queryKey: ['work', 'units', 'recent'] });
          void qc.invalidateQueries({ queryKey: ['work', 'station-daily'] });
          void qc.invalidateQueries({ queryKey: ['work', 'stats'] });
          void qc.invalidateQueries({ queryKey: ['work', 'leaderboard'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'work_breaks', filter: `organization_id=eq.${orgId}` },
        (payload) => {
          const row = (payload.new ?? payload.old) as Partial<{ session_id: string }>;
          if (row?.session_id) {
            void qc.invalidateQueries({ queryKey: workKeys.snapshot(row.session_id) });
            void qc.invalidateQueries({ queryKey: workKeys.sessionBreaks(row.session_id) });
          }
          void qc.invalidateQueries({ queryKey: workKeys.openBreaks() });
          void qc.invalidateQueries({ queryKey: workKeys.openSessions() });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'work_defects', filter: `organization_id=eq.${orgId}` },
        () => {
          void qc.invalidateQueries({ queryKey: ['work', 'defects'] });
          void qc.invalidateQueries({ queryKey: ['scrap'] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'work_notes', filter: `organization_id=eq.${orgId}` },
        () => void qc.invalidateQueries({ queryKey: ['work', 'notes'] }),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'serialized_units', filter: `organization_id=eq.${orgId}` },
        () => void qc.invalidateQueries({ queryKey: ['serialized'] }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled, orgId, qc]);
}

// ---------------------------------------------------------------------------
// BOM components (for "which parts are scrap?")
// ---------------------------------------------------------------------------

export interface BomComponent {
  item_id: string;
  sku: string;
  name: string;
  quantity: number;
  uom: string;
}

/** Component lines of the active BOM for an item (empty when there is none). */
export function useBomComponents(itemId: string | null) {
  return useQuery({
    queryKey: ['work', 'bom-components', itemId],
    enabled: !!itemId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<BomComponent[]> => {
      const { data, error } = await supabase
        .from('boms' as never)
        .select('id, bom_versions(id, is_active, version, bom_lines(component_item_id, quantity, uom, component:component_item_id(sku, name, base_uom)))')
        .eq('item_id', itemId!);
      if (error) throw error;
      type Row = {
        id: string;
        bom_versions: Array<{
          id: string;
          is_active: boolean;
          version: number;
          bom_lines: Array<{
            component_item_id: string;
            quantity: number | string;
            uom: string;
            component: { sku: string; name: string; base_uom: string | null } | null;
          }>;
        }>;
      };
      const rows = (data as unknown as Row[]) ?? [];
      const versions = rows.flatMap((r) => r.bom_versions ?? []);
      const active = versions.find((v) => v.is_active) ?? versions.sort((a, b) => b.version - a.version)[0];
      if (!active) return [];
      return (active.bom_lines ?? []).map((l) => ({
        item_id: l.component_item_id,
        sku: l.component?.sku ?? '',
        name: l.component?.name ?? 'Component',
        quantity: Number(l.quantity) || 1,
        uom: l.component?.base_uom || l.uom,
      }));
    },
  });
}

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

/** Index open sessions by workstation and by user. */
export function useOpenSessionIndex(sessions: WorkSession[] | undefined) {
  return useMemo(() => {
    const byStation = new Map<string, WorkSession>();
    const byUser = new Map<string, WorkSession>();
    for (const s of sessions ?? []) {
      byStation.set(s.workstation_id, s);
      byUser.set(s.user_id, s);
    }
    return { byStation, byUser };
  }, [sessions]);
}
